import React, { useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Column, Sheet } from "@amzn/meridian";
import "src/components/pages/inspection/cameraFrame.scss";
import { isEngineerUser } from "src/constants/constants";
import StreamingDevControls from "./streamingDevControls";
import ObjectInstanceSuccessSheet from "../questions/ObjectInstanceSuccessSheet";
import DetectionOverlay from "./detectionOverlay";
import InspectionResultSheet from "./components/InspectionResultSheet";
import ManualOverrideSheet from "./components/ManualOverrideSheet";
import CaptureButton from "./components/CaptureButton";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { setCurrentAnalyzedResult, resetCurrentAnalyzedResult } from "src/store/inspectionResultsSlice";
import { setImageCapturingMode, clearBarcodeData } from "src/store/inspectionCaptureSlice";
import { resetStreamingState as resetStreamingStateUtil } from "./utils";
import type { CameraFacingMode, InspectionOverrideData } from "./types";

// Custom hooks
import { useVideoCapture } from "./hooks/useVideoCapture";
import { useDualStreamCapture } from "./hooks/useDualStreamCapture";
import { useStreamingService } from "./hooks/useStreamingService";
import { useInspectionFlow } from "./hooks/useInspectionFlow";
import { useAutoCapture } from "./hooks/useAutoCapture";
import { useResultPolling } from "./hooks/useResultPolling";

// Handlers
import { handleCapture as handleCaptureUtil, handleAcceptAI, handleOverride } from "./handlers/inspectionHandlers";

type CameraFrameProps = {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  activeQuestionIndex: number;
  setActiveQuestionId: React.Dispatch<React.SetStateAction<string>>;
  setActiveQuestionIndex: React.Dispatch<React.SetStateAction<number>>;
  totalQuestions?: number;
  onCapture?: (file: File) => void;
  facingMode?: CameraFacingMode;
  onBackToList?: () => void;
};

const CameraFrame = ({
  videoRef,
  activeQuestionIndex,
  totalQuestions,
  setActiveQuestionIndex,
  onCapture,
  onBackToList,
}: CameraFrameProps) => {
  // Navigation
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  
  // Redux state
  const dispatch = useAppDispatch();
  const {
    imageCapturingMode,
    activeQuestion,
    activeObjectQuestions,
    capturedImageSrc,
    readyToSubmitReport,
    streamingMode,
    streamingDevMode,
    streamingConnected,
    detectionBoxes,
    streamingGuidanceMessage,
    imageNextStage,
    showCaptureAnimation,
    streamingMetrics,
    streamingConfig,
  } = useAppSelector((state) => state.inspectionCapture);
  
  const { currentCreatedInspection } = useAppSelector((state) => state.inspection);
  
  const {
    currentAnalyzedResult,
    inspectionResult,
    analyzeEvidenceAsyncLoading,
    inspectionResults,
  } = useAppSelector((state) => state.inspectionResults);

  const userLdapGroups = useAppSelector((state) => state.user.ldap);
  const isEngineer = useMemo(() => isEngineerUser(userLdapGroups), [userLdapGroups]);
  
  // Local state
  const [overrideReasonCode, setOverrideReasonCode] = useState("");
  const [openManualOverrideSheet, setOpenManualOverrideSheet] = useState(false);
  const [openInspectionResultSheet, setOpenInspectionResultSheet] = useState(false);
  const [openObjectSuccessSheet, setOpenObjectSuccessSheet] = useState(false);
  const [currentObjectType, setCurrentObjectType] = useState("");
  const [inspectionOverrideData, setInspectionOverrideData] = useState<InspectionOverrideData>({
    overrideResult: "",
    reasonCode: "",
    evidence: {
      evidenceId: "",
      fileType: "IMAGE_JPEG",
      file: "",
      uri: "",
    },
  });

  // Standard mode video capture
  const { videoWidth: standardVideoWidth, videoHeight: standardVideoHeight, captureFrame: standardCaptureFrame } = useVideoCapture({ videoRef });
  
  // Dual camera system for streaming mode (with screen-based resolution)
  const {
    lowResVideoRef,
    highResVideoRef,
    captureHighResFrame,
    captureLowResFrame,
    streamsReady,
    isDualMode
  } = useDualStreamCapture(
    streamingConfig.resolutionScale,  // Low-res scale from config
    1.0  // High-res always 100% of screen
  );
  
  const { streamingService, pauseStreaming, resumeStreaming } = useStreamingService({
    lowResVideoRef,
    highResVideoRef,
    enabled: streamingMode && streamsReady,
    onReadyForAnalyzer: () => {
      // Open success sheet when backend signals ready for analyzer
      // Check if this is object-based inspection (has multiple questions per object)
      if (activeObjectQuestions.length > 0) {
        setOpenObjectSuccessSheet(true);
      } else {
        // For non-object questions, open result sheet
        setOpenInspectionResultSheet(true);
      }
    },
  });
  
  // Initialize object type when entering object mode
  React.useEffect(() => {
    if (activeObjectQuestions.length > 0 && !currentObjectType) {
      setCurrentObjectType(activeObjectQuestions[0]?.applicableObjectTypes?.join(',') || 'Object');
    }
  }, [activeObjectQuestions, currentObjectType]);
  
  // Video dimensions - use streaming or standard based on mode
  const videoWidth = streamingMode ? (lowResVideoRef.current?.videoWidth || 0) : standardVideoWidth;
  const videoHeight = streamingMode ? (lowResVideoRef.current?.videoHeight || 0) : standardVideoHeight;
  
  useInspectionFlow();
  
  const captureCallback = useCallback(async () => {
    if (streamingMode) {
      pauseStreaming();
    }
    await handleCaptureInternal();
  }, [streamingMode, pauseStreaming]);
  
  const { isAutoCapturing } = useAutoCapture({
    streamingMode,
    captureCallback,
    isDialogOpen: openInspectionResultSheet,
  });

  // Poll for result updates when dialog is open and waiting for result
  useResultPolling({
    enabled: openInspectionResultSheet && !currentAnalyzedResult.aiResult,
    inspectionId: currentCreatedInspection.inspectionId,
    questionId: activeQuestion?.questionId || null,
    onResultReady: (questionResult) => {
      // Update currentAnalyzedResult with the completed analysis
      dispatch(setCurrentAnalyzedResult({
        aiResult: questionResult.aiResult,
        aiReasoning: questionResult.aiReasoning || "",
        analysisTimestamp: new Date().toISOString(),
      }));
    },
  });

  // Handlers
  const handleCaptureInternal = async () => {
    // Reset currentAnalyzedResult to clear stale data from previous question
    dispatch(resetCurrentAnalyzedResult());
    
    // Use appropriate capture method based on mode
    let capturedFile: File;
    if (streamingMode) {
      // Streaming mode: capture from high-res dual camera stream
      capturedFile = await captureHighResFrame();
    } else {
      // Standard mode: capture from original videoRef
      capturedFile = await standardCaptureFrame();
    }
    
    await handleCaptureUtil({
      captureFrame: async () => capturedFile,
      dispatch,
      currentCreatedInspectionId: currentCreatedInspection.inspectionId,
      activeQuestion,
      activeObjectQuestions,
      onOpenObjectSuccessSheet: (objectType) => {
        setCurrentObjectType(objectType);
        setOpenObjectSuccessSheet(true);
      },
      onOpenInspectionResultSheet: () => {
        setOpenInspectionResultSheet(true);
      },
    });
    
    if (onCapture) {
      onCapture(capturedFile);
    }
  };

  /**
   * Helper function to determine if current inspection is for drums
   */
  const isDrumInspection = useCallback(() => {
    if (!activeQuestion) return false;
    const objectTypes = activeQuestion.applicableObjectTypes;
    if (!objectTypes || objectTypes.length === 0) return false;
    return objectTypes.some(type => type.toLowerCase() === "drum");
  }, [activeQuestion]);

  const handleInspectNextInstance = () => {
    setOpenObjectSuccessSheet(false);
    
    // Check if this is a drum inspection
    const isDrum = isDrumInspection();
    
    if (isDrum) {
      // For drum inspections, navigate to barcode scanner for next drum
      // DON'T clear the camera modes - they need to persist for when we return
      dispatch(clearBarcodeData());
      navigate(`/templates/${templateId}/barcode-scan`);
    } else {
      // For non-drum inspections, resume capture mode
      if (streamingMode) {
        resumeStreaming();
      } else {
        dispatch(setImageCapturingMode(true));
      }
    }
  };

  const handleFinishObjectInspection = () => {
    setOpenObjectSuccessSheet(false);
    if (onBackToList) {
      onBackToList();
    }
  };

  const handleUploadEvidence = (file: File) => {
    setInspectionOverrideData((prev) => ({
      ...prev,
      evidence: { ...prev.evidence, file: file }
    }));
  };

  const handleAcceptAIClick = () => {
    handleAcceptAI({
      dispatch,
      activeQuestionId: activeQuestion?.questionId || "",
      inspectionResult,
      currentAnalyzedResult,
      capturedImageSrc,
      evidenceFile: inspectionOverrideData.evidence.file,
      streamingService,
      onClose: () => setOpenInspectionResultSheet(false),
      onBackToList,
    });
  };

  const handleOverrideSaveClick = () => {
    handleOverride({
      dispatch,
      activeQuestionId: activeQuestion?.questionId || "",
      inspectionResult,
      currentAnalyzedResult,
      capturedImageSrc,
      evidenceFile: inspectionOverrideData.evidence.file,
      reasonCode: inspectionOverrideData.reasonCode,
      streamingService,
      onClose: () => {
        setOpenInspectionResultSheet(false);
        setOpenManualOverrideSheet(false);
        // Reset override data
        setInspectionOverrideData({
          overrideResult: "",
          reasonCode: "",
          evidence: {
            evidenceId: "",
            fileType: "IMAGE_JPEG",
            file: "",
            uri: "",
          },
        });
        setOverrideReasonCode("");
      },
      onBackToList,
    });
  };

  const handleSkip = () => {
    setOpenInspectionResultSheet(false);
    resetStreamingStateUtil(dispatch);
    if (onBackToList) {
      onBackToList();
    }
  };

  const handleResultSheetClose = () => {
    setOpenInspectionResultSheet(false);
    resetStreamingStateUtil(dispatch);
    if (streamingMode) {
      resumeStreaming();
    }
  };

  return (
    <Column className="camera-frame-viewport">
      {/* Streaming mode: Display low-res video directly (high-res hidden) */}
      {streamingMode && (
        <>
          <video
            ref={lowResVideoRef}
            className="image-source-video"
            autoPlay
            muted
            playsInline
          />
          <video
            ref={highResVideoRef}
            autoPlay
            muted
            playsInline
            style={{ display: 'none' }}
          />
        </>
      )}
      
      {/* Standard camera mode (non-streaming) */}
      {imageCapturingMode && !streamingMode && (
        <video
          className="image-source-video"
          ref={videoRef}
          autoPlay
          muted
          playsInline
        />
      )}
      
      {/* Capture flash animation */}
      {showCaptureAnimation && (
        <div className="capture-flash-animation" />
      )}
      
      <div className="camera-frame-wrapper">
        {(imageCapturingMode || streamingMode) && (
          <Column className="camera-frame">
            <Column className="top-line-camera-frame" />
            <Column className="left-line-camera-frame" />
            <Column className="right-line-camera-frame" />
            <Column className="bottom-line-camera-frame" />
          </Column>
        )}
        
        {!imageCapturingMode && !streamingMode && (
          <div className="captured-image-display-card">
            <img src={capturedImageSrc} alt="Captured evidence" />
          </div>
        )}
        
      {/* Detection Overlay for streaming mode */}
      {streamingMode && lowResVideoRef.current && (
        <DetectionOverlay 
          detectionBoxes={detectionBoxes}
          videoWidth={videoWidth}
          videoHeight={videoHeight}
          imageNextStage={imageNextStage}
          guidanceMessage={streamingGuidanceMessage}
          autoCapturing={isAutoCapturing}
        />
      )}
      </div>

      {/* Main Inspection Result Sheet */}
      <Sheet
        className="ai-answer-dialogue-sheet"
        type="overlay"
        side="bottom"
        open={openInspectionResultSheet && !openManualOverrideSheet}
        onClose={handleResultSheetClose}
      >
        <div style={{ borderRadius: "21px" }}>
          <InspectionResultSheet
            open={true}
            isLoading={analyzeEvidenceAsyncLoading === "pending" || !currentAnalyzedResult.aiResult}
            aiResult={currentAnalyzedResult.aiResult}
            aiReasoning={currentAnalyzedResult.aiReasoning}
            streamingMode={streamingMode}
            streamingDevMode={streamingDevMode}
            streamingMetrics={streamingMetrics}
            onClose={handleResultSheetClose}
            onOverride={() => setOpenManualOverrideSheet(true)}
            onAccept={handleAcceptAIClick}
            onSkip={handleSkip}
          />
        </div>
      </Sheet>

      {/* Manual Override Sheet */}
      <Sheet
        className="ai-answer-dialogue-sheet"
        type="overlay"
        side="bottom"
        open={openManualOverrideSheet}
        onClose={() => setOpenManualOverrideSheet(false)}
      >
        <div style={{ borderRadius: "21px" }}>
          <ManualOverrideSheet
            overrideResult={inspectionOverrideData.overrideResult}
            reasonCode={overrideReasonCode}
            evidenceFile={inspectionOverrideData.evidence.file}
            aiResult={currentAnalyzedResult.aiResult}
            onResultChange={(result) => setInspectionOverrideData(prev => ({ ...prev, overrideResult: result }))}
            onReasonChange={(reason) => {
              setOverrideReasonCode(reason);
              setInspectionOverrideData(prev => ({ ...prev, reasonCode: reason }));
            }}
            onImageUpload={handleUploadEvidence}
            onSave={handleOverrideSaveClick}
            onCancel={() => setOpenManualOverrideSheet(false)}
          />
        </div>
      </Sheet>
      
      {/* Object Instance Success Sheet */}
      <ObjectInstanceSuccessSheet
        open={openObjectSuccessSheet}
        objectType={currentObjectType}
        isUploading={analyzeEvidenceAsyncLoading === "pending"}
        onInspectNext={handleInspectNextInstance}
        onFinish={handleFinishObjectInspection}
      />
      
      {/* Dev Mode Controls */}
      {streamingDevMode && streamingMode && streamingService && (
        <StreamingDevControls streamingService={streamingService} />
      )}
      
      {/* Connection Status */}
      {streamingDevMode && streamingMode && (
        <div className={`connection-status ${streamingConnected ? 'connected' : 'disconnected'}`}>
          {streamingConnected ? 
            (streamingService?.isStreamingPaused() ? 'Connected (Paused)' : 'Connected') : 
            'Connecting...'}
        </div>
      )}
      
      {/* Capture Button - Hidden when ready for analyzer or modal open */}
      {(imageCapturingMode || streamingMode) && 
       !readyToSubmitReport && 
       !openInspectionResultSheet && 
       !openObjectSuccessSheet && 
       imageNextStage !== 'READY_FOR_ANALYZER' && (
        <CaptureButton 
          onClick={streamingMode ? captureCallback : handleCaptureInternal}
        />
      )}
    </Column>
  );
};

export default CameraFrame;
